import { divide } from '@common/lib/decimal';
import {
  type ProductionRecipeItemProps,
  type WorkshopUpgradeItemAll,
  type WorkshopUpgradeItemInfoProps,
} from '@common/types/components';
import { type BoostItemType } from '@common/types/factoryType';
import {
  type ProductData,
  type UserWorkshop,
  type UserWorkshopNextToBuild,
  type WorkshopRequirements,
} from '@common/types/workshopType';
import { createSelector } from '@reduxjs/toolkit';

import { getProductionCycleTime } from '@/common/lib/getProductionCycleTime';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';

import { type RootState } from '../store';

import {
  calculateTotalBoost,
  checkIfBoostTimersEnd,
  checkIfFreeBoostAvailable,
  createRequirementsList,
  generateActiveBoostList,
  getActiveBoost,
  getMaxExpiresTimeByPlant,
  mapBoostForCalcMining,
  selectAdsBoost,
  selectBoostX5,
  selectBoostX10,
  selectFreeBoost,
  selectPaidBoost,
  selectVipBoost,
} from './lib';

const selectBoosts = (state: RootState): BoostItemType[] | null =>
  state.workshop.boosts;
const selectWorkshopList = (state: RootState): UserWorkshop[] | null =>
  state.workshop.list;

const selectWorkshopNextToBuild = (
  state: RootState,
): UserWorkshopNextToBuild | null => state.workshop.nextToBuild;

const selectWorkshopNextToBuildCreation = (
  state: RootState,
): WorkshopRequirements | undefined =>
  state.workshop.nextToBuild?.creationRequirements;

const selectNextToBuildWorkshopRequirementsList = createSelector(
  selectWorkshopNextToBuildCreation,
  createRequirementsList,
);

const selectWorkshopByNameOrId = createSelector(
  selectWorkshopList,
  (_: RootState, key?: string): string | undefined => key,
  (
    workshops: UserWorkshop[] | null,
    key?: string,
  ): UserWorkshop | undefined => {
    if (workshops && key) {
      return workshops.find(({ id, name }) => id === key || name === key);
    }
  },
);

const selectWorkshopUpgradeData = createSelector(
  selectWorkshopList,
  (_: RootState, id: string | undefined): string | undefined => id,
  (workshops, id): WorkshopUpgradeItemAll | undefined => {
    if (workshops && id) {
      const workshop = workshops.find((workshop) => workshop.id === id);

      if (workshop) {
        const nextLevel = workshop.nextLevelRequirements;

        const result: WorkshopUpgradeItemInfoProps[] = [];

        const load = {
          kind: 'load',
          now: getProductionCycleTime(
            workshop.products[0].storageCapacityOriginal,
            workshop.products[0].productionRateOriginal,
          ),
          next: getProductionCycleTime(
            nextLevel.products[0].storageCapacity,
            nextLevel.products[0].productionRate,
          ),
        };
        workshop.products.forEach((product) => {
          const goodsName = product.name;
          const nextLevelProduct = nextLevel.products.find(
            (product) => product.name === goodsName,
          );

          if (nextLevelProduct) {
            const now = divide(3600, product.productionRateOriginal).toFixed(2);
            const next = divide(3600, nextLevelProduct.productionRate).toFixed(
              2,
            );
            result.push({ goodsName, now, next });
          }
        });

        return {
          load,
          result,
        };

        // const load = {
        //   kind: 'load',
        //   now: getProductionCycleTime(factory.storageCapacity, factory.productionRate),
        //   next: getProductionCycleTime(nextLevel.storageCapacity, nextLevel.productionRate)
        // };
      }
    }
  },
);

const selectLastProductDataByWorkshopId = createSelector(
  selectWorkshopList,
  (_: RootState, id: string): string => id,
  (workshops, id): ProductData | undefined => {
    if (workshops) {
      const workshop = workshops.find(
        (workshop) => workshop.id === id || workshop.name === id,
      );

      if (workshop) {
        return workshop.products[workshop.products.length - 1];
      }
    }
  },
);

const selectTargetGoodsByWorkshopId = createSelector(
  selectWorkshopList,
  (_: RootState, id?: string): string | undefined => id,
  (workshops, id): ProductData | undefined => {
    if (workshops && id) {
      const workshop = workshops.find(
        (workshop) => workshop.id === id || workshop.name === id,
      );

      if (workshop) {
        return workshop.products.find(
          (product) => product.name === workshop.targetProduct,
        );
      }
    }
  },
);

const selectWorkshopProductByName = createSelector(
  selectWorkshopList,
  (
    _: RootState,
    args: { workshopId?: string; goodsName?: GoodsType },
  ): { workshopId?: string; goodsName?: GoodsType } => args,
  (workshops, { workshopId, goodsName }): ProductData | undefined => {
    const workshop = workshops?.find(
      (workshop) => workshop.id === workshopId || workshop.name === workshopId,
    );

    if (workshop) {
      const name = goodsName || workshop.targetProduct;

      return workshop.products.find((product, index) =>
        name ? product.name === name : index === 0,
      );
    }
  },
);

const selectWorkshopBoostList = createSelector(
  selectBoosts,
  generateActiveBoostList,
);

const selectFreeBoostForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectFreeBoost,
);

const selectAdsBoostForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectAdsBoost,
);

const selectVipBoostForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectVipBoost,
);

const selectPaidBoostForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectPaidBoost,
);

const selectBoostX5ForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectBoostX5,
);
const selectBoostX10ForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId?: string): string | undefined => plantId,
  selectBoostX10,
);

const selectTotalWorkshopBoostById = createSelector(
  selectBoosts,
  (_: RootState, plantId: string | undefined): string | undefined => plantId,
  calculateTotalBoost,
);

const getMaxExpiresTimeForWorkshopBoosts = createSelector(
  selectBoosts,
  (_: RootState, plantId: string): string => plantId,
  getMaxExpiresTimeByPlant,
);

const selectBoostForWorkshop = createSelector(
  selectBoosts,
  (_: RootState, plantId: string | undefined): string | undefined => plantId,
  mapBoostForCalcMining,
);

const selectActiveBoostForWorkshop = createSelector(
  selectBoosts,
  getActiveBoost,
);

const boostWorkshopTimersEnd = createSelector(
  selectBoosts,
  checkIfBoostTimersEnd,
);

const workshopFreeBoostAvailable = createSelector(
  selectBoosts,
  checkIfFreeBoostAvailable,
);

const selectShortWorkshopInfo = createSelector(
  selectWorkshopList,
  (workshops) => workshops?.map(({ name, id }) => ({ name, id })),
);

const selectGoodsListByWorkshop = createSelector(
  selectWorkshopByNameOrId,
  (
    workshop,
  ):
    | { list: (ProductData | undefined)[]; availableQuantity: number }
    | undefined => {
    if (workshop) {
      const list: (ProductData | undefined)[] = Array.from({ length: 4 });
      let availableQuantity = 0;

      workshop.products.forEach((goods, index) => {
        list[index] = goods;
        ++availableQuantity;
      });

      return { list, availableQuantity };
    }
  },
);

const selectGoodsRecipeByGoodsName = createSelector(
  [
    selectWorkshopList,
    (
      _: RootState,
      workshopId?: string,
      _goodsName?: GoodsType,
    ): string | undefined => workshopId,
    (
      _: RootState,
      _workshopId?: string,
      goodsName?: GoodsType,
    ): GoodsType | undefined => goodsName,
  ],
  (
    workshops,
    workshopId,
    goodsName,
  ): ProductionRecipeItemProps[] | undefined => {
    if (workshops && goodsName && workshopId) {
      const workshop = workshops.find(
        ({ id, name }) => workshopId === id || workshopId === name,
      );

      const goods = workshop?.products?.find(({ name }) => name === goodsName);

      if (goods) {
        return Object.entries(goods.recipeForProduction).map(
          ([name, quantity]) => ({ name, quantity }),
        );
      }
    }
  },
);

const selectGoodsByMaterialRecipe = createSelector(
  selectWorkshopList,
  (
    _: RootState,
    material: PlantTypeText | undefined,
  ): PlantTypeText | undefined => material,
  (workshops, material) => {
    const result: { id: string; goodsName: GoodsType }[] = [];

    if (workshops && material) {
      for (const workshop of workshops) {
        for (const goods of workshop.products) {
          const recipeNames = Object.keys(goods.recipeForProduction);
          const recipeSet = new Set(recipeNames);

          if (recipeSet.has(material)) {
            result.push({ id: workshop.id, goodsName: goods.name });
          }
        }
      }
    }

    return result;
  },
);

export {
  boostWorkshopTimersEnd,
  getMaxExpiresTimeForWorkshopBoosts,
  selectActiveBoostForWorkshop,
  selectAdsBoostForWorkshop,
  selectBoostForWorkshop,
  selectBoostX5ForWorkshop,
  selectBoostX10ForWorkshop,
  selectFreeBoostForWorkshop,
  selectGoodsByMaterialRecipe,
  selectGoodsListByWorkshop,
  selectGoodsRecipeByGoodsName,
  selectLastProductDataByWorkshopId,
  selectNextToBuildWorkshopRequirementsList,
  selectPaidBoostForWorkshop,
  selectShortWorkshopInfo,
  selectTargetGoodsByWorkshopId,
  selectTotalWorkshopBoostById,
  selectVipBoostForWorkshop,
  selectWorkshopBoostList,
  // selectWorkshopBoostsListById,
  selectWorkshopByNameOrId,
  selectWorkshopList,
  selectWorkshopNextToBuild,
  selectWorkshopProductByName,
  selectWorkshopUpgradeData,
  workshopFreeBoostAvailable,
};
